import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatValue } from './DataTableCard';

const SERIES = [
  { key: 'revenue', label: 'Revenue', color: '#16a34a', format: 'currency' },
  { key: 'sessions', label: 'Sessions', color: '#2563eb', format: 'number' },
  { key: 'cost', label: 'Ad Spend', color: '#dc2626', format: 'currency' },
  { key: 'roas', label: 'ROAS', color: '#7c3aed', format: 'multiplier' },
];

const TrendChartCard = ({ card }) => {
  const [activeKey, setActiveKey] = useState('revenue');
  if (!card || !card.dailyData || card.dailyData.length === 0) return null;

  const activeSeries = SERIES.find(s => s.key === activeKey) || SERIES[0];

  // Compute change% for each pill from card.metrics
  const metricMap = {};
  if (card.metrics) {
    card.metrics.forEach(m => { metricMap[m.seriesKey] = m; });
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm w-full mt-3">
      {card.title && (
        <div className="text-xs font-semibold text-gray-700 mb-2">{card.title}</div>
      )}

      {/* Metric toggle pills */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {SERIES.map(s => {
          const m = metricMap[s.key];
          const changePct = m && m.previous ? ((m.current - m.previous) / m.previous) * 100 : null;
          const isActive = activeKey === s.key;
          return (
            <button
              key={s.key}
              onClick={() => setActiveKey(s.key)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium transition-colors ${
                isActive
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={isActive ? { backgroundColor: s.color } : undefined}
            >
              {s.label}
              {changePct !== null && (
                <span className={`ml-0.5 ${isActive ? 'opacity-80' : changePct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {changePct >= 0 ? '↑' : '↓'}{Math.abs(changePct).toFixed(1)}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={card.dailyData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`grad-${activeKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={activeSeries.color} stopOpacity={0.2} />
              <stop offset="95%" stopColor={activeSeries.color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            tickFormatter={d => d ? d.slice(5) : ''}
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis hide />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
            formatter={(val) => [formatValue(val, activeSeries.format, activeKey), activeSeries.label]}
            labelFormatter={l => l ? l.slice(5) : ''}
          />
          <Area
            type="monotone"
            dataKey={activeKey}
            stroke={activeSeries.color}
            strokeWidth={2}
            fill={`url(#grad-${activeKey})`}
            dot={false}
            activeDot={{ r: 3 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      {card.period && (
        <div className="mt-2 text-[10px] text-gray-400 text-right font-mono">
          {card.period.start} &rarr; {card.period.end}
        </div>
      )}
    </div>
  );
};

export default TrendChartCard;
